import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment
} from '@mui/material';

import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import api from 'api/axios';

// ---------- helpers ----------
const toE164ID = (v) => {
  let s = String(v ?? '').trim();
  if (!s) return null;

  s = s.replace(/[^\d+]/g, '');
  if (!s) return null;

  if (s.startsWith('+')) s = s.slice(1);

  if (s.startsWith('62')) return '+' + s;
  if (s.startsWith('0')) return '+62' + s.slice(1);
  if (s.startsWith('8')) return '+62' + s;
  if (/^\d{10,15}$/.test(s)) return '+' + s;

  return null;
};

const guessDelimiter = (sampleLine) => {
  const candidates = [',', ';', '\t', '|'];
  let best = ',';
  let bestCount = -1;
  for (const d of candidates) {
    const c = (sampleLine.match(new RegExp(`\\${d}`, 'g')) || []).length;
    if (c > bestCount) {
      bestCount = c;
      best = d;
    }
  }
  return best;
};

const splitCols = (line, delimiter) =>
  String(line || '')
    .split(delimiter)
    .map((s) => s.trim());

async function parseTxtCsv(file, { userIdMode = false } = {}) {
  const text = await file.text();

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const delimiter = guessDelimiter(lines[0] || ',');
  const headerCols = splitCols(lines[0], delimiter).map((h) => h.toLowerCase());

  const phoneIdx = headerCols.findIndex((h) => /phone|tel|nomor|wa|whatsapp/.test(h));
  const userIdIdx = headerCols.findIndex((h) => /(^|\b)(user\s*id|userid|user_id|uid|id_user)(\b|$)/.test(h));
  const nameIdx = headerCols.findIndex((h) => /name|nama|user/.test(h));

  const hasHeader = phoneIdx !== -1 || userIdIdx !== -1 || nameIdx !== -1;

  const rawRows = [];

  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    const cols = splitCols(lines[i], delimiter);
    if (!cols.length) continue;

    let phoneRaw = '';
    let secondRaw = '';

    if (hasHeader) {
      phoneRaw = phoneIdx !== -1 ? cols[phoneIdx] || '' : cols[0] || '';

      if (userIdMode) {
        if (userIdIdx !== -1) secondRaw = cols[userIdIdx] || '';
        else if (nameIdx !== -1) secondRaw = cols[nameIdx] || '';
        else secondRaw = cols[0] || '';
      } else {
        secondRaw = nameIdx !== -1 ? cols[nameIdx] || '' : cols[0] || '';
      }
    } else {
      // no header
      const a = cols[0] || '';
      const b = cols[1] || '';

      const aE = toE164ID(a);
      const bE = toE164ID(b);

      if (aE && !bE) {
        phoneRaw = a;
        secondRaw = b;
      } else if (!aE && bE) {
        phoneRaw = b;
        secondRaw = a;
      } else {
        phoneRaw = a;
        secondRaw = b;
      }
    }

    const phone = toE164ID(phoneRaw);
    if (!phone) continue;

    rawRows.push({
      phone,
      second: String(secondRaw || '').trim()
    });
  }

  // dedupe by normalized phone
  const seen = new Set();
  const base = Date.now();
  const result = [];

  for (const r of rawRows) {
    if (!r.phone) continue;
    if (seen.has(r.phone)) continue;
    seen.add(r.phone);

    result.push({
      id: `${base}-${result.length}`,
      phone: r.phone,
      userId: r.second || ''
    });
  }

  return result;
}

const normalizeUrl = (v) => {
  const s = String(v || '').trim();
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) return `https://${s}`;
  return s;
};

const stripHttp = (v) =>
  String(v || '')
    .trim()
    .replace(/^https?:\/\//i, '');

// ---------- "NO MUI" chip multi-select ----------
function SessionMultiSelect({ options, value, onChange, placeholder = 'Select sessions...' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = Array.isArray(options) ? options : [];
    if (!q) return base;
    return base.filter((x) => String(x).toLowerCase().includes(q));
  }, [options, filter]);

  const add = (name) => {
    if (!name) return;
    if (selectedSet.has(name)) return;
    onChange([...selected, name]);
  };

  const remove = (name) => {
    onChange(selected.filter((x) => x !== name));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: 42,
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 10,
          padding: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          background: 'rgba(255,255,255,0.02)',
          cursor: 'text'
        }}
      >
        {selected.length === 0 ? (
          <span style={{ opacity: 0.6, fontSize: 14 }}>{placeholder}</span>
        ) : (
          selected.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.10)',
                fontSize: 13
              }}
            >
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                }}
              >
                {s}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: 1
                }}
                aria-label={`remove-${s}`}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 8,
            border: '1px solid rgba(255,255,255,0.16)',
            borderRadius: 12,
            background: '#111318',
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: 10, borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter..."
              style={{
                width: '100%',
                height: 36,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.16)',
                background: 'rgba(255,255,255,0.04)',
                color: 'white',
                padding: '0 12px',
                outline: 'none'
              }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, opacity: 0.7, fontSize: 14 }}>No sessions</div>
            ) : (
              filtered.map((name) => {
                const isSel = selectedSet.has(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => add(name)}
                    disabled={isSel}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 0,
                      background: isSel ? 'rgba(255,255,255,0.08)' : 'transparent',
                      color: 'white',
                      cursor: isSel ? 'not-allowed' : 'pointer',
                      opacity: isSel ? 0.65 : 1
                    }}
                    title={isSel ? 'Already selected' : 'Select'}
                  >
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      }}
                    >
                      {name}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              padding: 10,
              borderTop: '1px solid rgba(255,255,255,0.10)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end'
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setFilter('');
              }}
              style={{
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'transparent',
                color: 'white',
                borderRadius: 10,
                padding: '8px 10px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                border: 0,
                background: 'rgba(255,255,255,0.12)',
                color: 'white',
                borderRadius: 10,
                padding: '8px 10px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- component ----------
export default function Message() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  const [statusText, setStatusText] = useState('');

  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const [intervalSeconds, setIntervalSeconds] = useState(2);
  const [maxMessagesPerSession, setMaxMessagesPerSession] = useState(1);

  const [messageText, setMessageText] = useState('');
  const [linkButtons, setLinkButtons] = useState([{ label: 'Link 1', url: '' }]);

  const [recipientsFile, setRecipientsFile] = useState(null);
  const fileRef = useRef(null);

  const [imageFile, setImageFile] = useState(null);
  const imgRef = useRef(null);
  const [imagePreview, setImagePreview] = useState('');

  const [userIdMode, setUserIdMode] = useState(false);

  const [recipientsPreview, setRecipientsPreview] = useState([]);
  const [recPage, setRecPage] = useState(1);
  const [recPerPage, setRecPerPage] = useState(25);

  const [isSending, setIsSending] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);

  const [bulkJobs, setBulkJobs] = useState([]); // [{ jobId, sessionName }]
  const [isStopping, setIsStopping] = useState(false);

  const recTotalPages = Math.max(1, Math.ceil(recipientsPreview.length / Math.max(1, recPerPage)));
  const pagedRecipients = useMemo(() => {
    const start = (recPage - 1) * recPerPage;
    return recipientsPreview.slice(start, start + recPerPage);
  }, [recipientsPreview, recPage, recPerPage]);

  useEffect(() => setRecPage(1), [recipientsPreview, recPerPage]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const fetchActiveSessions = async () => {
    try {
      const res = await api.get('api/sessions/all/100/1?active=true', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.sessions) ? payload.sessions : [];

      const uniq = [];
      const seen = new Set();
      list.forEach((s) => {
        const name = s?.sessionName;
        if (!name || seen.has(name)) return;
        seen.add(name);
        uniq.push(name);
      });

      setSessionOptions(uniq);
    } catch (e) {
      console.error('fetch sessions list error:', e);
      setSessionOptions([]);
      setStatusText('Gagal mengambil daftar session.');
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAll = () => {
    setRecipientsFile(null);
    setRecipientsPreview([]);
    setRecPage(1);
    setBulkSummary(null);
    setBulkJobs([]);
    setIsStopping(false);
    setStatusText('');

    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    setImageFile(null);

    setUserIdMode(false);
  };

  const detectUserIdHeader = async (file) => {
    const text = await file.text();
    const firstLine =
      text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)[0] || '';
    const delimiter = guessDelimiter(firstLine || ',');
    const headerCols = splitCols(firstLine, delimiter).map((h) => h.toLowerCase());
    return headerCols.some((h) => /(^|\b)(user\s*id|userid|user_id|uid|id_user)(\b|$)/.test(h));
  };

  const parseAndPreview = async (file, mode) => {
    try {
      const rows = await parseTxtCsv(file, { userIdMode: mode });
      setRecipientsPreview(rows);
    } catch {
      setRecipientsPreview([]);
      setStatusText('Gagal parsing file recipients.');
    }
  };

  const onRecipientsChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setRecipientsFile(f);
    setStatusText('');

    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (ext === 'txt') {
      setUserIdMode(false);
      await parseAndPreview(f, false);
    } else if (ext === 'csv') {
      const hasUserId = await detectUserIdHeader(f);
      setUserIdMode(hasUserId);
      await parseAndPreview(f, hasUserId);
    } else {
      setRecipientsPreview([]);
      setUserIdMode(false);
      setStatusText('Format file tidak didukung. Upload .txt atau .csv.');
    }

    e.target.value = '';
  };

  const onImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setImageFile(f);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(f));

    e.target.value = '';
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    setImageFile(null);
  };

  // ------- link buttons helpers (UNLIMITED) -------
  const addLinkRow = () => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const n = list.length + 1;
      list.push({ label: `Link ${n}`, url: '' });
      return list;
    });
  };

  const removeLinkRow = (idx) => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      list.splice(idx, 1);
      return list.map((x, i) => ({ ...x, label: `Link ${i + 1}` }));
    });
  };

  const updateLinkRow = (idx, key, value) => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const row = list[idx] || { label: `Link ${idx + 1}`, url: '' };
      list[idx] = { ...row, [key]: value };
      return list;
    });
  };

  const buildTemplateFromInputs = () => {
    const body = String(messageText || '').trim();

    const tpl = {
      name: 'promo_button',
      components: [
        {
          type: 'body',
          parameters: [{ type: 'text', text: body }]
        }
      ]
    };

    const filled = (Array.isArray(linkButtons) ? linkButtons : [])
      .map((b, i) => ({
        label: String(b?.label || `Link ${i + 1}`).trim() || `Link ${i + 1}`,
        url: normalizeUrl(b?.url)
      }))
      .filter((x) => !!x.url);

    filled.forEach((x, idx) => {
      tpl.components.push({
        type: 'button',
        sub_type: 'url',
        index: String(idx),
        text: x.label || `Link ${idx + 1}`,
        parameters: [{ type: 'text', text: x.url }]
      });
    });

    return { tpl, body, links: filled };
  };

  const startBulkSend = async () => {
    setStatusText('');

    if (!selectedSessions.length) return setStatusText('Pilih minimal 1 sessionName.');
    if (!recipientsFile) return setStatusText('File recipients belum dipilih.');
    if (recipientsPreview.length === 0) return setStatusText('Recipients kosong / tidak valid.');

    const { tpl, body, links } = buildTemplateFromInputs();
    if (!body) return setStatusText('Message wajib diisi.');

    for (const b of links) {
      if (b.url && !/^https?:\/\/\S+$/i.test(b.url)) {
        return setStatusText(`Format link tidak valid: ${b.url}`);
      }
    }

    setIsSending(true);
    setBulkSummary(null);
    setBulkJobs([]);

    try {
      const fd = new FormData();
      fd.append('sessionNames', selectedSessions.join(','));
      fd.append('intervalSeconds', String(Math.max(0, Number(intervalSeconds || 0))));
      fd.append('maxMessagesPerSession', String(Math.max(1, Number(maxMessagesPerSession || 1))));
      fd.append('recipients', recipientsFile);
      fd.append('userId', 'true');
      if (imageFile) fd.append('image', imageFile);
      fd.append('template', JSON.stringify(tpl));

      const res = await api.post('/api/messages/bulk', fd, AUTH);
      const payload = res?.data || {};

      setBulkJobs(Array.isArray(payload.jobs) ? payload.jobs : []);

      setBulkSummary({
        intervalSeconds: payload.intervalSeconds,
        totalMessages: payload.totalMessages,
        totalRecipients: payload.totalRecipients,
        totalSessions: payload.totalSessions
      });

      // sesuai request: send tidak perlu modal/alert
    } catch (err) {
      console.error(err);
      setStatusText(err?.response?.data?.message || err?.message || 'Gagal bulk send');
    } finally {
      setIsSending(false);
    }
  };

  const stopBulkSend = async () => {
    const jobs = Array.isArray(bulkJobs) ? bulkJobs : [];
    if (jobs.length === 0) return;

    setStatusText('');
    setIsStopping(true);

    try {
      const results = await Promise.allSettled(
        jobs.map((j) => {
          const sessionName = j?.sessionName || '';
          const jobId = j?.jobId;

          if (!sessionName || !jobId) {
            return Promise.reject(new Error('Missing sessionName/jobId'));
          }

          const url = `/api/sessions/${encodeURIComponent(sessionName)}/messages/${encodeURIComponent(jobId)}/cancel`;
          return api.post(url, null, AUTH);
        })
      );

      const failed = results.filter((r) => r.status === 'rejected').length;

      // sesuai request:
      // - cancel tetap jalan
      // - kalau gagal, jangan bilang cek console -> bilang "semua pesan sudah terkirim"
      if (failed > 0) {
        setStatusText('Semua pesan sudah terkirim.');
      } else {
        setStatusText('Pengiriman dibatalkan.');
      }

      setBulkJobs([]);
    } catch (e) {
      console.error(e);
      // juga treat sebagai "sudah terkirim"
      setStatusText('Semua pesan sudah terkirim.');
      setBulkJobs([]);
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Bulk Message Sender" />
            <CardContent>
              <Grid container spacing={2}>
                {/* LEFT */}
                <Grid item xs={12} md={5}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        sessionNames (multi)
                      </Typography>

                      <SessionMultiSelect
                        options={sessionOptions}
                        value={selectedSessions}
                        onChange={setSelectedSessions}
                        placeholder="Choose active sessions..."
                      />

                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedSessions(sessionOptions)}
                          disabled={sessionOptions.length === 0}
                        >
                          Select All
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setSelectedSessions([])}
                          disabled={selectedSessions.length === 0}
                        >
                          Clear
                        </Button>

                        <Button size="small" variant="outlined" onClick={fetchActiveSessions}>
                          Refresh sessions
                        </Button>

                        <Box sx={{ flex: 1 }} />

                        <Typography variant="caption" sx={{ opacity: 0.75, alignSelf: 'center' }}>
                          Selected: <b>{selectedSessions.length}</b> / {sessionOptions.length}
                        </Typography>
                      </Stack>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="intervalSeconds"
                          type="number"
                          fullWidth
                          value={intervalSeconds}
                          onChange={(e) => setIntervalSeconds(Math.max(0, Number(e.target.value || 0)))}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="maxMessagesPerSession"
                          type="number"
                          fullWidth
                          value={maxMessagesPerSession}
                          onChange={(e) => setMaxMessagesPerSession(Math.max(1, Number(e.target.value || 1)))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      label="Message (template body) *"
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={12}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder={`Contoh:\nAyo Segera Main`}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                          e.preventDefault();
                          startBulkSend();
                        }
                      }}
                    />

                    <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Buttons URL (optional, unlimited)</Typography>
                        <Button size="small" variant="outlined" onClick={addLinkRow}>
                          Add button
                        </Button>
                      </Stack>

                      {linkButtons.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.75 }}>
                          Tidak ada button (opsional). Klik &quot;Add button&quot; untuk menambah.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {linkButtons.map((b, idx) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label={`Text #${idx + 1}`}
                                value={b.label || `Link ${idx + 1}`}
                                onChange={(e) => updateLinkRow(idx, 'label', e.target.value)}
                                sx={{ flex: 1 }}
                                placeholder={`Link ${idx + 1}`}
                              />

                              <TextField
                                label={`URL #${idx + 1}`}
                                value={stripHttp(b.url)}
                                onChange={(e) => {
                                  const stripped = stripHttp(e.target.value);
                                  updateLinkRow(idx, 'url', stripped);
                                }}
                                sx={{ flex: 2 }}
                                placeholder="abc.com"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">https://</InputAdornment>
                                }}
                              />

                              <Tooltip title="Remove">
                                <IconButton size="small" color="error" onClick={() => removeLinkRow(idx)}>
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Tip: ketik domain saja (abc.com). Kalau paste https://abc.com tetap aman.
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        recipients (TXT/CSV file)
                      </Typography>

                      <input ref={fileRef} type="file" hidden accept=".txt,.csv" onChange={onRecipientsChange} />
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={() => fileRef.current?.click()}>
                          Choose recipients
                        </Button>
                        <Button variant="outlined" color="error" onClick={clearAll}>
                          Clear
                        </Button>
                      </Stack>

                      {recipientsFile && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                          File: <b>{recipientsFile.name}</b> • userIdMode(detected): <b>{String(userIdMode)}</b>
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        media (image / video, optional)
                      </Typography>

                      <input ref={imgRef} type="file" hidden accept="image/*,video/*" onChange={onImageChange} />

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={() => imgRef.current?.click()}>
                          Choose media
                        </Button>
                        <Button variant="outlined" color="error" onClick={removeImage} disabled={!imageFile}>
                          Remove
                        </Button>
                      </Stack>

                      {imageFile && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                          Media: <b>{imageFile.name}</b>{' '}
                          <span style={{ fontSize: 12, opacity: 0.7 }}>({imageFile.type || 'unknown type'})</span>
                        </Typography>
                      )}

                      {imagePreview && (
                        <Box sx={{ mt: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.75 }}>
                            Preview
                          </Typography>

                          {imageFile?.type?.startsWith('video') ? (
                            <Box
                              component="video"
                              src={imagePreview}
                              controls
                              sx={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 1 }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={imagePreview}
                              alt="Preview"
                              sx={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 1 }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={startBulkSend}
                        disabled={isSending || isStopping || !recipientsFile || selectedSessions.length === 0}
                      >
                        {isSending ? 'Sending...' : 'Send Bulk'}
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={stopBulkSend}
                        disabled={isSending || isStopping || bulkJobs.length === 0}
                      >
                        {isStopping ? 'Stopping...' : 'Stop'}
                      </Button>
                    </Stack>

                    {!!statusText && (
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {statusText}
                      </Typography>
                    )}

                    {bulkSummary && (
                      <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Summary:
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          intervalSeconds: <b>{bulkSummary.intervalSeconds}</b>
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          totalSessions: <b>{bulkSummary.totalSessions}</b> • totalRecipients: <b>{bulkSummary.totalRecipients}</b> •
                          totalMessages: <b>{bulkSummary.totalMessages}</b>
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* RIGHT */}
                <Grid item xs={12} md={7}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Recipients Preview ({recipientsPreview.length})
                  </Typography>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr">
                          {['#', 'Phone', 'User ID'].map((h) => (
                            <Box
                              key={h}
                              component="th"
                              sx={{
                                textAlign: 'left',
                                fontWeight: 600,
                                p: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              {h}
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      <Box component="tbody">
                        {recipientsPreview.length === 0 ? (
                          <Box component="tr">
                            <Box component="td" colSpan={3} sx={{ p: 1, opacity: 0.8 }}>
                              Upload TXT/CSV untuk preview
                            </Box>
                          </Box>
                        ) : (
                          pagedRecipients.map((r, i) => {
                            const startIndex = (recPage - 1) * recPerPage;
                            return (
                              <Box component="tr" key={r.id}>
                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {startIndex + i + 1}
                                </Box>

                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {r.phone}
                                  </Typography>
                                </Box>

                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {r.userId || '-'}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total: {recipientsPreview.length} | Page {recPage} / {recTotalPages}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Rows:
                      </Typography>
                      <Select size="small" value={recPerPage} onChange={(e) => setRecPerPage(Number(e.target.value))}>
                        {[25, 50, 100, 200].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button size="small" onClick={() => setRecPage((p) => Math.max(1, p - 1))} disabled={recPage <= 1}>
                        Prev
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setRecPage((p) => Math.min(recTotalPages, p + 1))}
                        disabled={recPage >= recTotalPages}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
